<template>
  <FieldFrame v-bind="frameBindings">
    <template
      v-for="slotName in forwardedFieldSlots()"
      :key="slotName"
      #[slotName]
    >
      <slot :name="slotName" />
    </template>

    <template
      v-if="hasCharacterLimit(maxlength)"
      #hint
    >
      <FieldCharacterCount
        :value="modelValue"
        :maxlength="maxlength"
      />
    </template>

    <template #default="control">
      <UTextarea
        ref="input"
        v-bind="{ ...controlAttributes, ...fieldControlAria(control) }"
        :model-value="modelValue"
        :name="name"
        :rows="rows"
        :placeholder="placeholder"
        :disabled="disabled"
        size="md"
        variant="none"
        fixed
        :ui="controlUi"
        @update:model-value="handleUpdate"
        @blur="handleBlur"
      />
    </template>
  </FieldFrame>
</template>

<script setup lang="ts">
import { toRefs, useTemplateRef } from 'vue'
import UTextarea from '@nuxt/ui/components/Textarea.vue'
import { createStringInputHandlers } from '~/utils/createStringInputHandlers'

import FieldCharacterCount from '../atoms/FieldCharacterCount/FieldCharacterCount.atom.vue'
import FieldFrame from './FieldFrame/FieldFrame.molecule.vue'
import { useFieldFrame } from './FieldFrame/useFieldFrame'
import { fieldControlAria } from '../base/fieldControlAria'
import { hasCharacterLimit } from '../base/hasCharacterLimit'
import { useFieldPresentation } from '~/design-system/molecules/useFieldPresentation'
import { textEntryFieldDefaults } from '~/design-system/molecules/textEntryFieldDefaults'
import type { TextEntryFieldProps } from '~/interfaces/TextEntryFieldProps'
import type { InputFieldEvents } from '~/interfaces/InputFieldEvents'
import type { FieldLabelSlots } from '~/interfaces/FieldLabelSlots'

const props = withDefaults(
  defineProps<
    TextEntryFieldProps & {
      /** Visible text rows. */
      rows?: number
    }
  >(),
  {
    ...textEntryFieldDefaults,
    rows: 4
  }
)

const emit = defineEmits<InputFieldEvents<string>>()

defineSlots<FieldLabelSlots>()

// UTextarea's generated props lack an index signature; one v-bind spread forwards aria-required.
const { error, help, hint, label, name, required, serverError } = toRefs(props)
const {
  message,
  controlUi,
  inputAttributes: controlAttributes
} = useFieldPresentation({ error, help, required, serverError })
const { frameBindings, forwardedFieldSlots } = useFieldFrame(
  { help, hint, label, name, required },
  message,
  ['help', 'label']
)

// No id reaches UTextarea on purpose: the field owns the control id. See
// "Let the field own the control id" in docs/design-system-planes.md.

const { handleUpdate, handleBlur } = createStringInputHandlers(emit)

const input = useTemplateRef<{ textareaRef?: HTMLTextAreaElement | null }>('input')

defineExpose({
  focus: () => input.value?.textareaRef?.focus()
})
</script>
